import styles from './CalendarFluidLabels.module.css';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AvatarItem } from '@he-novation/design-system/components/avatars/Avatar/AvatarItem';
import { AvatarUser } from '@he-novation/design-system/components/avatars/Avatar/AvatarUser';
import { initialsColor } from '@he-novation/utils/colors';
import cn from 'classnames';

import combineSelectors from '$helpers/combineSelectors';
import { useTranslate } from '$hooks/useTranslate';
import { clientUsersSelector } from '$redux/client/clientSelectors';
import { clientContactsSelector } from '$redux/content/contacts/contactsSelectors';
import { Item } from '$types/items';

type CalendarFluidLabelUserProps = {
    uuid: string;
    isCurrentUser?: boolean;
    email: string;
    firstname?: string | null;
    lastname?: string | null;
    picture?: string | null;
};

export const CalendarFluidLabelUser = ({
    uuid,
    email,
    firstname,
    lastname,
    picture,
    isCurrentUser
}: CalendarFluidLabelUserProps) => {
    const { t } = useTranslate();
    const { clientUsers, clientContacts } = useSelector(
        combineSelectors(clientUsersSelector, clientContactsSelector)
    );
    const users = useMemo(
        () => [...(clientUsers || []), ...(clientContacts || [])],
        [clientUsers, clientContacts]
    );
    const user = users.find((user) => user?.uuid === uuid);

    const renderName = () => {
        if (isCurrentUser) {
            return t('calendar.You');
        }
        if (firstname && lastname) {
            return `${firstname} ${lastname.toUpperCase()}`;
        }

        return email;
    };

    return (
        <div className={styles.label}>
            <AvatarUser
                picture={user?.picture || picture}
                email={email}
                firstname={firstname}
                lastname={lastname}
                className={styles.avatar}
            />
            <span className={styles.text}>{renderName()}</span>
        </div>
    );
};

type CalendarFluidLabelItemProps = {
    item: Item;
};

export const CalendarFluidLabelItem = ({ item }: CalendarFluidLabelItemProps) => {
    return (
        <div className={cn(styles.label, styles.item)}>
            <AvatarItem
                uuid={item.uuid}
                name={item.name}
                picture={item.picture}
                className={styles.avatar}
            />
            <span
                style={{ color: initialsColor(item.name[0] + item.name[1]) }}
                className={styles.text}
            >
                {item.name}
            </span>
        </div>
    );
};

type CalendarFluidLabelLabelProps = {
    name: string;
    color: string;
};

export const CalendarFluidLabelLabel = ({ name, color }: CalendarFluidLabelLabelProps) => {
    return (
        <div className={cn(styles.label, styles.item)}>
            <div className={styles.dot} style={{ backgroundColor: color }} />
            <span style={{ color: color }} className={styles.text}>
                {name}
            </span>
        </div>
    );
};
